/* ==================================================================
   The Golden Quest · API layer
   GET /golden-quest (the page payload, with the streaks scored server-side
   from the user's transactions) and POST /golden-quest/action (one user
   action, picked by `action`) — gosats-apis, goSatsCampaignRoot. Maps
   `resp.goldenQuest` onto the model the page renders; the static copy in
   campaign.js is the fallback for anything the backend doesn't send.
   ================================================================== */
import * as APIHelper from '../Helper/apiFetch';
import { GQ_MOCK_RESPONSES, GQ_MOCK_SCENARIO, goldReward, satsReward } from './mockResponse';
import {
  LAUNCH, END, DRAW_ON, PRE_REG_GEMS, PRE_REG_EARNED_AT_FROM, PRE_REG_EARNED_AT, REFERRAL_CODE, GRAND_PRIZE_COUNT, PENDING_REWARD,
  STREAKS, STREAK_NAME, GEM_SOURCE_LABEL, GRAND_TOP, GRAND_ALL, PRIZE_TIERS, TERMS, FAQS,
  SURVEY_COPY, SURVEY_CHEST_NAME, shareMessageFor, phaseFromDates,
} from './campaign';
import { doorFor } from './survey';

/* Outside production builds, `?scenario=<preset>` on the URL serves a preset
   from mockResponse.js instead of calling the backend, so every flow previews
   in a plain browser without a session token. Without it the page talks to
   the API. */
const MOCK_SCENARIO = new URLSearchParams(window.location.search).get('scenario');
const USE_MOCK = process.env.NODE_ENV !== 'production' && MOCK_SCENARIO !== null;
const MOCK_LATENCY_MS = 400;

const STATUS_PHASE = { PRE_LAUNCH: 'pre', LIVE: 'live', ENDED: 'ended' };

const mockDelay = (value, ms = MOCK_LATENCY_MS) => new Promise((resolve) => setTimeout(() => resolve(value), ms));

function mockResponse() {
  return GQ_MOCK_RESPONSES[MOCK_SCENARIO] || GQ_MOCK_RESPONSES[GQ_MOCK_SCENARIO];
}

const toDate = (v, fallback) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d : fallback;
};

/* the static streak definitions (icons, sizes) overlaid with the API's copy
   and progress; the open streaks are listed first, the locked ones after
   (each group in the design's order) */
function mapStreaks(apiStreaks) {
  const byKey = {};
  (apiStreaks || []).forEach((s) => { if (s && s.key) byKey[s.key] = s; });
  const streaks = STREAKS.map((s) => {
    const api = byKey[s.key] || {};
    return {
      ...s,
      /* the backend decides when a streak opens: `enabled: false` → the card
         is locked (lock chip, "Unlocks" CTA), whatever the campaign phase */
      locked: api.enabled === false,
      title: api.title || s.title,
      desc: api.text || s.desc,
      labels: Array.isArray(api.labels) && api.labels.length === 5 ? api.labels : s.labels,
      cta: (api.cta && api.cta.text) || s.cta,
      action: (api.cta && api.cta.action) || null,
      progress: Number.isFinite(api.progress) ? Math.max(0, Math.min(api.progress, 4)) : 0,
      completed: api.completed === true,
      extraGems: Number.isFinite(api.extraGems) ? api.extraGems : 0,
    };
  });
  return [...streaks.filter((s) => !s.locked), ...streaks.filter((s) => s.locked)];
}

/* rupees for the reward lines: ₹3 · ₹0.25 · ₹1,250 */
const fmtInr = (n) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/* what an opened Gem holds (and, from reveal day, what a Sats/Gold chest
   held). The backend credits Sats or Gold — the user's pick, `pref` — and
   sends the order it placed:
     { type: 'bonus_sats' | 'bonus_gold', value, inr, pref, code, orderId, price, pool }
   `inr` is the rupee worth, `value` the amount credited (Sats; gold in mg at
   `price` per gram), `code` the reward tier (G1, G2… / P1 for pre-registration)
   and `pool` where it was drawn from. Mapped to { kind, name, detail } for the
   reward cells; the pre-shaped { name, detail, kind } is still accepted. An
   opened Gem with `reward: null` shows as pending rather than as unopened (its
   open is already recorded, so it can't be opened again). */
export function mapReward(r) {
  if (!r) return PENDING_REWARD;
  const extra = { inr: Number.isFinite(+r.inr) ? +r.inr : null, value: Number.isFinite(+r.value) ? +r.value : null, orderId: r.orderId || null, code: r.code || null };
  if (r.name) return { name: r.name, detail: r.detail || '', kind: r.kind || '', ...extra };
  const type = String(r.type || r.pref || r.kind || '').toUpperCase();
  const kind = /SATS|BTC|BITCOIN/.test(type) ? 'SATS' : (/GOLD/.test(type) ? 'GOLD' : '');
  if (!kind) return PENDING_REWARD;
  const { inr, value } = extra;
  if (kind === 'SATS') {
    const name = value > 0 ? `${Math.round(value).toLocaleString('en-IN')} Sats` : `${inr > 0 ? fmtInr(inr) + ' in ' : ''}Bitcoin`;
    const detail = `${value > 0 && inr > 0 ? `Worth ${fmtInr(inr)} · ` : ''}Added to your Bitcoin balance`;
    return { name, detail, kind, ...extra };
  }
  return { name: inr > 0 ? `${fmtInr(inr)} in Gold` : 'Gold', detail: 'Added to your gold vault', kind, ...extra };
}

/* a Gem: where it came from, when, and — once opened — what it held
   (`openedAt` + `reward` in the API; `opened: { at, reward } | null` here).
   `uid` tells Gems apart on the page: the backend can repeat an `id` (one
   `ref-2` per referral batch, say), and OPEN_GEM takes the `id`. */
export function mapGem(g, i = 0) {
  const opened = g.openedAt
    ? { at: toDate(g.openedAt, new Date()), reward: mapReward(g.reward) }
    : null;
  const id = String(g.id ?? `gem-${i}`);
  /* a PRE_REG Gem stamped at launch midnight reads 18 Oct, 6:00 PM (see PRE_REG_EARNED_AT) */
  const earnedRaw = typeof g.earnedAt === 'string' && /^\d+$/.test(g.earnedAt) ? +g.earnedAt : g.earnedAt;
  const earned = toDate(earnedRaw, LAUNCH);
  const fixEarned = g.source === 'PRE_REG' && earnedRaw != null && earned.getTime() === PRE_REG_EARNED_AT_FROM;
  return {
    id,
    uid: id,
    source: g.source || '',
    sourceLabel: GEM_SOURCE_LABEL[g.source] || g.source || 'Gem',
    earnedAt: fixEarned ? PRE_REG_EARNED_AT : earned,
    opened,
  };
}

/* the wallet's Gems, with a page-unique `uid` each (`ref-2`, `ref-2·2`, `ref-2·3`
   when the backend repeats an id) */
export function mapGems(list) {
  const seen = {};
  return (Array.isArray(list) ? list : []).map((g, i) => {
    const gem = mapGem(g, i);
    seen[gem.id] = (seen[gem.id] || 0) + 1;
    return seen[gem.id] > 1 ? { ...gem, uid: `${gem.id}·${seen[gem.id]}` } : gem;
  });
}

/* a Gold Chest: the streak that earned it (or the survey, `streak: 'survey'`),
   and what came of it on reveal day — `result` names a grand prize
   ({ kind: 'GRAND', prize, rank }); a Sats/Gold chest comes with `openedAt`
   and the credited `reward` (the Gem reward shape), read as its result. */
export function mapChest(c, i = 0) {
  /* the survey's chest is `streak: 'survey'`, whichever case the backend writes it in */
  const streak = /^survey$/i.test(c.streak || '') ? 'survey' : (c.streak || '');
  const reward = c.reward ? mapReward(c.reward) : null;
  let result = null;
  if (c.result && c.result.kind) result = { kind: c.result.kind, prize: c.result.prize || null, rank: c.result.rank || null };
  else if (reward && reward.kind !== 'PENDING') result = { kind: reward.kind, prize: reward.name, rank: null };
  return {
    id: String(c.id ?? `chest-${i}`),
    streak,
    streakName: STREAK_NAME[streak] || (streak === 'survey' ? SURVEY_CHEST_NAME : streak) || 'Streak',
    earnedAt: toDate(c.earnedAt, new Date()),
    celebrated: c.celebrated !== false,
    openedAt: c.openedAt ? toDate(c.openedAt, new Date()) : null,
    reward,
    result,
  };
}

function mapGrandPrizes(gp) {
  const top = gp && Array.isArray(gp.top) && gp.top.length
    ? gp.top.map((p) => ({ art: p.art, name: p.name, sub: p.sub, win: p.rank, badge: p.badge }))
    : GRAND_TOP;
  const all = gp && Array.isArray(gp.all) && gp.all.length ? gp.all : GRAND_ALL;
  const tiers = gp && Array.isArray(gp.tiers) && gp.tiers.length ? gp.tiers : PRIZE_TIERS;
  const sum = (list) => list.reduce((n, p) => n + (p.winners || 0), 0);
  return {
    total: (gp && gp.total) || GRAND_PRIZE_COUNT,
    drawOn: toDate(gp && gp.drawOn, DRAW_ON),
    top,
    all,
    tiers,
    tierWinners: (gp && gp.tierWinners) || sum(tiers),
    totalPrizes: (gp && gp.totalPrizes) || ((gp && gp.total) || GRAND_PRIZE_COUNT) + ((gp && gp.tierWinners) || sum(tiers)),
  };
}

/* ---- the quick survey (`goldenQuest.survey`) ----
   One opening question (`questions.q1`) whose answer picks a door —
   `questions.doors["1"|"2"|"3"]`, the follow-up questions — and a Golden
   Chest for finishing. `copy` is every string the survey UI shows (the
   static SURVEY_COPY fills any the backend leaves out). `progress` is the
   user's saved answers when they left midway; `status` / `chestId` say
   whether it's new, in progress or done. */
function mapQuestion(q, fallbackId) {
  if (!q || !Array.isArray(q.opts) || !q.opts.length) return null;
  const many = q.type === 'many';
  return {
    id: q.id || fallbackId,
    q: q.q || '',
    hint: q.hint || (many ? 'Select all that apply' : 'Pick one'),
    type: many ? 'many' : 'one',
    /* `max: 99` is the backend's "no limit" */
    max: many && Number.isFinite(q.max) && q.max > 0 ? q.max : 99,
    grid: q.grid === true,
    opts: q.opts.map(String),
  };
}

/* saved answers: `{ answers: { [questionId]: index | index[] }, door }`,
   accepting a bare answers map too */
function mapSurveyProgress(p) {
  if (!p || typeof p !== 'object') return { answers: {}, door: null };
  const raw = p.answers || p.ans || (p.door === undefined ? p : {});
  const answers = {};
  Object.keys(raw || {}).forEach((k) => {
    const v = raw[k];
    if (Array.isArray(v)) answers[k] = v.map(Number).filter(Number.isInteger);
    else if (Number.isInteger(v)) answers[k] = v;
    else if (v !== null && v !== undefined && Number.isInteger(+v)) answers[k] = +v;
  });
  return { answers, door: p.door !== undefined && p.door !== null ? String(p.door) : null };
}

export function mapSurvey(sv) {
  if (!sv || sv.enabled === false) return null;
  const qs = sv.questions || {};
  const q1 = mapQuestion(qs.q1, 'q1');
  if (!q1) return null; // nothing to ask
  const doors = {};
  Object.keys(qs.doors || {}).forEach((k) => {
    const list = Array.isArray(qs.doors[k]) ? qs.doors[k].map((q, i) => mapQuestion(q, `d${k}q${i + 1}`)).filter(Boolean) : [];
    if (list.length) doors[k] = list;
  });
  const progress = mapSurveyProgress(sv.progress);
  const survey = { copy: { ...SURVEY_COPY, ...(sv.copy || {}) }, q1, doors, takeable: sv.takeable !== false };
  if (!progress.door) progress.door = doorFor(survey, progress.answers);
  const done = sv.status === 'done' || sv.status === 'completed' || !!sv.chestId;
  const started = !done && Object.keys(progress.answers).length > 0;
  return {
    ...survey,
    /* the user's standing, kept as page state once loaded */
    state: {
      status: done ? 'done' : (started ? 'part' : 'new'),
      answers: done ? {} : progress.answers,
      door: done ? null : progress.door,
      chestId: sv.chestId ? String(sv.chestId) : null,
    },
  };
}

/* grand draw results (ENDED); null until the draw is published. `you.rank`
   points at the user's own row in `winners`, which renders as "You". */
function mapDraw(d) {
  if (!d || !Array.isArray(d.winners)) return null;
  const you = d.you || {};
  const won = you.won === true;
  const rank = Number.isFinite(you.rank) ? you.rank : null;
  return {
    drawnAt: toDate(d.drawnAt, DRAW_ON),
    you: {
      won,
      rank,
      prize: you.prize || null,
      title: you.title || (won ? `You won ${you.prize ? 'a ' + you.prize : 'a grand prize'}` : 'No grand prize this time'),
      text: you.text || (won ? 'Shipping to your registered address.' : 'Your Gems and their rewards are still yours.'),
      kicker: you.kicker || (won ? 'Delivery details on their way by SMS' : 'See you at the next festival'),
    },
    winners: d.winners.map((w, i) => {
      const isYou = won && rank === i + 1;
      return { name: isYou ? 'You' : w.name, city: w.city || '', prize: w.prize, isYou };
    }),
  };
}

/* maps resp.goldenQuest → page model, or null when the campaign is off */
export function mapGoldenQuest(resp) {
  const data = resp && (resp.data ?? resp);
  const gq = data && data.goldenQuest;
  if (!gq || gq.enabled === false) return null;

  const launch = toDate(gq.campaignStart, LAUNCH);
  const end = toDate(gq.campaignEnd, END);
  const user = gq.user || {};
  const wallet = gq.wallet || {};
  const registered = user.preRegistered === true;
  const preRegGems = Number.isFinite(user.preRegGems) ? user.preRegGems : PRE_REG_GEMS;
  const referralCode = user.referralCode || REFERRAL_CODE;
  const gems = mapGems(wallet.gems);
  const chests = Array.isArray(wallet.chests) ? wallet.chests.map(mapChest) : [];
  /* the backend's terms, as sections (see campaign.js TERMS) or the older flat
     `items` list; the static copy only when it sends neither */
  const hasList = (v) => Array.isArray(v) && v.length > 0;
  const terms = gq.terms && (hasList(gq.terms.sections) || hasList(gq.terms.items)) ? gq.terms : TERMS;

  return {
    phase: STATUS_PHASE[gq.status] || phaseFromDates(launch, end),
    launch,
    end,
    registered,
    preRegGems,
    notify: user.reminderSet === true,
    storyEnabled: !(gq.story && gq.story.enabled === false),
    storySeen: user.storySeen === true,
    liveIntroSeen: user.liveIntroSeen === true,
    gems,
    chests,
    referralCode,
    shareMessage: (gq.share && gq.share.message) || shareMessageFor(referralCode),
    streaks: mapStreaks(gq.streaks),
    grandPrizes: mapGrandPrizes(gq.grandPrizes),
    draw: mapDraw(gq.draw),
    terms: { summary: terms.summary || '', sections: terms.sections || null, items: terms.items || null },
    faqs: Array.isArray(gq.faqs) && gq.faqs.length ? gq.faqs : FAQS,
    survey: mapSurvey(gq.survey), // null when the backend doesn't run one
  };
}

/* Resolves to the page model, or null when the campaign is disabled (nothing
   to show). A genuine network / auth failure throws so the page can retry. */
export async function fetchGoldenQuest() {
  if (USE_MOCK) return mapGoldenQuest(await mockDelay(mockResponse()));

  const res = await APIHelper.goldenQuest();
  const data = res && (res.data ?? res);
  /* `{}` is apiFetch's answer while there is no session token yet: a failure,
     not a disabled campaign, so the session listener can re-run the load */
  if (!res || res.error || !data || !data.goldenQuest) {
    throw new Error((res && res.message) || 'Golden Quest request failed');
  }
  return mapGoldenQuest(res);
}

/* ---- write calls ----
   All go through POST /golden-quest/action, picked by `action`. The backend
   answers `{ error: false, ... }` on success and `{ error: true, message }`
   when it declines (pre-registration closed, Gem locked or unknown, chest
   unknown); apiFetch answers `{}` without a session token. Anything but a
   success throws; a decline carries the backend's message with `refused` set
   so the page can show it as is. While mocking they resolve with the
   backend's shapes (and the prototype's timing) so the flows can depend on
   them. */

async function sendAction(body) {
  const res = await APIHelper.goldenQuestAction(body);
  if (res && res.error === false) return res;
  const err = new Error((res && res.message) || `Golden Quest ${body.action} failed`);
  err.refused = !!(res && res.error === true && res.message);
  throw err;
}

/* the user has watched the story; the next open skips it (`user.storySeen`) */
export async function markStorySeen() {
  if (USE_MOCK) return { error: false, storySeen: true };
  return sendAction({ action: 'STORY_SEEN' });
}

/* the Gems pre-registration reserves, as the backend reports them */
function mockPreRegGems() {
  return Array.from({ length: PRE_REG_GEMS }, (_, i) => ({
    id: `prereg-${i + 1}`, source: 'PRE_REG', earnedAt: LAUNCH.getTime(), openedAt: null, reward: null,
  }));
}

/* Claims the free pre-registration Gems. `notify` is the "Remind me when it
   starts" toggle: it sets the launch reminder, and the backend tracks the
   claim (with the toggle) only when it is sent. Resolves to
   { preRegistered, reminderSet, gems[] } — the Gems the backend reserved,
   with the ids OPEN_GEM accepts from launch. A repeat reports the stored
   registration (`alreadyRegistered`). */
export async function preRegister({ notify }) {
  if (USE_MOCK) {
    /* ~0.9s like the prototype's "Adding your Gems…" beat */
    return mockDelay({ error: false, preRegistered: true, reminderSet: notify === true, gems: mockPreRegGems() }, 900);
  }
  return sendAction({ action: 'PRE_REGISTER', notify: notify === true });
}

/* turns the launch reminder on / off (`user.reminderSet`) */
export async function setReminder(on) {
  if (USE_MOCK) return { error: false, reminderSet: on === true };
  return sendAction({ action: 'REMINDER', on: on === true });
}

/* the user has seen the "festival is open" interstitial (`user.liveIntroSeen`) */
export async function markLiveIntroSeen() {
  if (USE_MOCK) return { error: false, liveIntroSeen: true };
  return sendAction({ action: 'LIVE_INTRO_SEEN' });
}

/* the user has seen the celebration for a chest (`wallet.chests[].celebrated`) */
export async function markChestCelebrated(chestId) {
  if (USE_MOCK) return { error: false, chestId, celebrated: true };
  return sendAction({ action: 'CHEST_CELEBRATED', chestId: String(chestId) });
}

/* what a mock Gem can hold — the backend's reward shape (see mapReward) */
const MOCK_REWARDS = [
  goldReward(3, 'GSPRO11427'), satsReward(5, 'GSPRO11417', 'G2'), goldReward(10, 'GSPRO11430', 'G3'),
  satsReward(3, 'GSPRO11418'), goldReward(25, 'GSPRO11431', 'G4'), satsReward(10, 'GSPRO11432', 'G3'),
];

/* Opens a Gem: POST { action: 'OPEN_GEM', gemId } →
     { error, gemId, openedAt, reward, alreadyOpened }
   Resolves to { reward: { kind, name, detail… }, openedAt: Date, alreadyOpened }
   — the reward is decided server-side; the page only reveals it. A Gem that is
   already open comes back with `alreadyOpened: true` and what it holds, and is
   shown rather than failed (the wallet on the page was stale). */
export async function openGem(gemId) {
  let res;
  if (USE_MOCK) {
    /* deterministic per Gem so a preview is repeatable; ~0.75s like the prototype's shake */
    const n = String(gemId).split('').reduce((h, ch) => h + ch.charCodeAt(0), 0);
    const reward = MOCK_REWARDS[(n * 7 + 3) % MOCK_REWARDS.length];
    res = await mockDelay({ error: false, gemId, openedAt: Date.now(), reward, alreadyOpened: false }, 750);
  } else {
    res = await sendAction({ action: 'OPEN_GEM', gemId: String(gemId) });
  }
  return { reward: mapReward(res.reward), openedAt: toDate(res.openedAt, new Date()), alreadyOpened: res.alreadyOpened === true };
}

/* ---- the quick survey ----
   `answers` is { [questionId]: optionIndex | optionIndex[] } (see survey.js),
   e.g. { q1: 0, d1q1: [0, 3], d1q2: 1, d1q3: [0, 2], d1q4: 0 }. The door
   (which follow-up set) follows from the opening answer, so it isn't sent. */

/* the user left midway: the backend keeps the answers so far (`survey.progress`).
   NOTE: SURVEY_PROGRESS is the frontend's proposal — only SURVEY_SUBMIT is
   confirmed; a declined save is logged and the answers stay in page state. */
export async function saveSurveyProgress({ answers }) {
  if (USE_MOCK) return { error: false, saved: true };
  return sendAction({ action: 'SURVEY_PROGRESS', answers });
}

/* the last answer is in: POST { action: 'SURVEY_SUBMIT', answers }. The
   backend records the survey and credits the Golden Chest; the page reads
   the chest off the response with surveyChestFrom. */
export async function submitSurvey({ answers }) {
  if (USE_MOCK) {
    /* the claim beat covers ~0.6s of latency. A chest claimed once the
       scenario's reveal day (grandPrizes.drawOn) has passed comes with its
       result, as the backend's would, so the page can open it on the spot. */
    const gp = (mockResponse().goldenQuest || {}).grandPrizes || {};
    const drawn = gp.drawOn && new Date(gp.drawOn).getTime() <= Date.now();
    return mockDelay({
      error: false,
      chest: { id: `survey-${Date.now()}`, streak: 'survey', earnedAt: Date.now(), celebrated: true, openedAt: drawn ? Date.now() : null, result: drawn ? { kind: 'GRAND', prize: 'Apple Watch SE', rank: 3 } : null, reward: null },
    }, 600);
  }
  return sendAction({ action: 'SURVEY_SUBMIT', answers });
}

/* ---- analytics ----
   POST /analytics/event { event, properties? } — user events for the
   dashboards. Fire-and-forget: the page never waits on one, and a failed or
   declined event is only logged. While mocking nothing is sent; the event is
   logged to the console instead so the flow can be checked. */
export function trackEvent(event, properties) {
  const body = properties ? { event, properties } : { event };
  if (USE_MOCK) {
    console.info('[golden quest] analytics event (mock, not sent):', body);
    return Promise.resolve();
  }
  return APIHelper.analyticsEvent(body)
    .then((res) => { if (res && res.error) console.warn(`analytics event "${event}" declined:`, res.message); })
    .catch((err) => console.warn(`analytics event "${event}" failed:`, err));
}

/* the user began the survey (a fresh start, not a resume) */
export function trackSurveyStarted() {
  return trackEvent('Survey Started');
}

/* one question answered and submitted (Next / Finish): the question's id and
   the chosen option's text — the options' texts joined with " | " for a
   select-many question */
export function trackSurveyAnswer(question, answer) {
  return trackEvent('Survey QA', { question, answer });
}

/* the credited chest in a SURVEY_SUBMIT response: `chest` itself, the survey
   chest in an updated `wallet` (top-level or under `goldenQuest`), or a
   `chestId` (top-level or under `survey`); a placeholder when it names none,
   so the claim moment can still play — the next load has the real one. */
export function surveyChestFrom(res) {
  const r = res || {};
  const wallet = r.wallet || (r.goldenQuest && r.goldenQuest.wallet);
  const inWallet = wallet && Array.isArray(wallet.chests)
    ? wallet.chests.find((c) => c && /^survey$/i.test(c.streak || ''))
    : null;
  const raw = r.chest || inWallet;
  if (raw) return mapChest(raw);
  const id = r.chestId || (r.survey && r.survey.chestId);
  return mapChest({ id: id || `survey-${Date.now()}`, streak: 'survey', earnedAt: Date.now(), celebrated: true, result: null });
}
